import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
const Terms = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-background text-foreground">
      <SEO title="Terms of Service | Klexu" description="The terms governing use of Klexu's website, tools and consulting services." path="/terms" />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <Button variant="ghost" className="mb-8 text-muted-foreground hover:text-white" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Terms of Service</h1>
          
          <div className="glass-card p-8 rounded-xl">
            <div className="max-w-full space-y-8">
              <div>
                <p className="text-sm text-muted-foreground mb-6">
                  <strong>Version:</strong> 1.1 | <strong>Last Updated:</strong> December 3, 2025 | 
                  <strong>Previous Version:</strong> August 4, 2024 (available upon request)
                </p>
                
                <div className="space-y-6 text-muted-foreground">
                  <section>
                    <h2 className="text-xl font-bold mb-3">1. Territorial Scope and Applicability</h2>
                    <p className="mb-4">
                      These terms and conditions ("Terms" or "T&C") shall apply to all supplies, services and licenses of any kind provided by Klexu Resources SRL ("Klexu", "we", "us", "our") to its customers ("Customer(s)", "you", "your") located in the European Economic Area (EEA) and the United States of America (USA).
                    </p>
                    <p className="mb-4">
                      These Terms apply to all business relationships between Klexu and Customers, whether conducted online, offline, or through any other means. If Klexu and the Customer have entered into a separate contract of sale, service agreement, license agreement, or other Contract, these Terms shall apply except if and to the extent that such separate Contract provides otherwise.
                    </p>
                    <p className="mb-4">
                      Klexu provides services exclusively to business customers and does not provide supplies or services to individual consumers for personal, family, or household purposes.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">2. Scope</h2>
                    <p className="mb-4">
                      These terms and conditions shall apply to all supplies, services and licenses of any kind provided by Klexu to its customers. If Klexu and the Customer have entered into a contract of sale or a service, license or other Contract (hereafter collectively referred to as "Contract"), these Conditions shall apply except if and to the extent that the Contract provides otherwise.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">3. Definitions</h2>
                    <ul className="list-disc list-inside space-y-2">
                      <li>"Deliverables" – designates the goods, rights, licenses, or tangible or intangible works which are the subject matter of the Contract.</li>
                      <li>"Consumers" – designates natural persons who are not entering into the Contract for a purpose within the scope of their trade, business or independent profession.</li>
                      <li>"EEA" – The European Economic Area, comprising all 27 European Union member states plus Iceland, Liechtenstein, and Norway.</li>
                      <li>"USA" – The United States of America, including all 50 states, the District of Columbia, and U.S. territories.</li>
                      <li>"Business Customer" – designates legal entities, companies, organizations, or natural persons acting within the scope of their trade, business, or profession.</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">4. No Other Terms and Conditions</h2>
                    <p className="mb-4">
                      Klexu does not accept any terms and conditions which deviate from these T&C, except if such terms and conditions have been expressly accepted by Klexu in writing. Any quotations which Klexu may provide shall not be binding on Klexu unless explicitly stated otherwise.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">5. Confidentiality of Technical Specifications</h2>
                    <p className="mb-4">
                      The Customer shall refrain from using for any purpose unrelated to the Contract, copying and making available to third parties any technical specifications, drawings, quotations and calculations which the Customer may receive from Klexu, in particular in connection with the negotiation or the performance of the Contract. Klexu reserves the copyright and other intellectual property rights in such materials. In the event that the Customer violates the foregoing obligation, Klexu shall be entitled to request immediate release and delivery of such materials.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">6. Changes to Contract Scope</h2>
                    <p className="mb-4">
                      If Customer requests a change to the scope agreed in the Contract, Klexu will reasonably attempt to accommodate this request provided that the Customer will bear all extra cost and effort caused by the change. Any change to the scope requires mutual written agreement.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">7. Compliance with Local Regulations</h2>
                    <p className="mb-4">
                      Klexu shall comply with applicable laws and regulations in both the EEA and USA in the performance of its services. Customer acknowledges that certain services may be subject to export controls, sanctions, or other regulatory requirements that may affect the provision of services.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">8. Data Protection and Privacy</h2>
                    <p className="mb-4">
                      The processing of personal data in connection with these Terms shall be governed by Klexu's Privacy Policy, which complies with GDPR for EEA customers and applicable federal and state privacy laws for USA customers. Customer acknowledges that they have read and understood the Privacy Policy.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">9. Governing Law and Jurisdiction</h2>
                    <p className="mb-4">
                      These Terms of Service are governed by Romanian law, regardless of the Customer's location in the EEA or USA. 
                    </p>
                    <p className="mb-4">
                      Any disputes, controversies, or claims arising out of or relating to these Terms, or the breach, termination, enforcement, interpretation, or validity thereof, shall be submitted to the exclusive jurisdiction of the competent courts of Cluj-Napoca, Romania.
                    </p>
                    <p className="mb-4">
                      Klexu retains the right to initiate legal proceedings in the courts of the Customer's jurisdiction for collection of outstanding amounts or enforcement of intellectual property rights.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">10. Dispute Resolution</h2>
                    <p className="mb-4">
                      Prior to initiating formal legal proceedings, the parties agree to attempt resolution of disputes through good faith negotiations. If such negotiations fail within thirty (30) days, either party may proceed to formal dispute resolution through the courts of Cluj-Napoca, Romania.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">11. Force Majeure</h2>
                    <p className="mb-4">
                      Neither party shall be liable for any failure or delay in performance due to circumstances beyond their reasonable control, including but not limited to acts of God, war, terrorism, pandemic, government actions, or changes in applicable laws affecting cross-border business operations between the EEA and USA.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-bold mb-3">12. Contact Information</h2>
                    <p className="mb-4">
                      Email: <a href="mailto:admin@klexu.com" className="text-primary hover:underline">admin@klexu.com</a>
                    </p>
                  </section>
                </div>
                
                <div className="pt-8 mt-8 border-t border-border">
                  <h2 className="text-xl font-bold mb-4">Terms of Service Changelog</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Version 1.1 (December 3, 2025)</h3>
                      <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                        <li>Updated territorial scope and contact information</li>
                        <li>Added changelog section</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold mb-2">Version 1.0 (August 4, 2024)</h3>
                      <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                        <li>Initial terms publication</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default Terms;